import type { ConnectableAssistant } from '@/components/integrations/assistants/assistant-providers';
import { Alert } from '@/components/ui/alert';
import { PlanSuspendedBanner } from '@/components/subscription/plan-limit/PlanSuspendedBanner';
import { useAuth } from '@/contexts/auth-context';
import { useMcpConnectionsQuery } from '@/hooks/queries/use-mcp-query';
import { usePlanGates } from '@/hooks/use-plan-gates';
import { PlanLimit, upgradeTierFor, type McpConnection } from '@fluenceur/types';
import { Button, Skeleton, Stack, Text } from '@chakra-ui/react';
import { useTranslation } from 'react-i18next';
import { ConnectedAssistant } from './ConnectedAssistant';

/** Shared account-owned permissions: integrations filter by provider, never by brand. */
export function ConnectedAssistantsManagement({ provider }: { provider?: ConnectableAssistant }) {
  const { t } = useTranslation();
  const query = useMcpConnectionsQuery();
  const activeConnections = query.data?.connections.filter((connection) =>
    !connection.revokedAt && (!provider || connection.client.provider === provider)
  );
  const { user } = useAuth();
  const { planTier, mcpLocked } = usePlanGates();
  // The plan read is the current organisation's: only its connections are
  // known to be suspended. Listing, narrowing and revoking stay open.
  const organizationId = user?.currentBrand?.organizationId;
  const isSuspended = (connection: McpConnection) =>
    mcpLocked && (!organizationId || connection.organization.id === organizationId);
  const hasSuspended = activeConnections?.some(isSuspended) ?? false;
  return (
      <Stack gap={6}>
        <Text color="fg.muted">{t('mcp.settings.description')}</Text>
        {hasSuspended && (
          <PlanSuspendedBanner
            title={t('planSuspended.mcp.title')}
            body={t('planSuspended.mcp.body')}
            currentTier={planTier}
            targetTier={upgradeTierFor(PlanLimit.MCP, planTier)}
          />
        )}
        {query.isLoading && <Skeleton h="200px" aria-label={t('mcp.loading')} />}
        {query.isError && (
          <Stack gap={3}>
            <Alert status="error" title={t('mcp.settings.loadError')} />
            <Button variant="outline" alignSelf="start" onClick={() => void query.refetch()}>
              {t('mcp.retry')}
            </Button>
          </Stack>
        )}
        {activeConnections?.length === 0 && <BoxEmpty />}
        {activeConnections?.map((connection) => (
          <ConnectedAssistant
            key={`${connection.id}:${connection.consentVersion}:${connection.brandIds.join(',')}:${connection.scopes.join(',')}:${connection.revokedAt}`}
            connection={connection}
            suspended={isSuspended(connection)}
          />
        ))}
      </Stack>
  );
}


function BoxEmpty() {
  const { t } = useTranslation();
  return (
    <Stack borderWidth="1px" borderRadius="lg" p={6}>
      <Text fontWeight="semibold">{t('mcp.settings.empty')}</Text>
      <Text color="fg.muted">{t('mcp.settings.emptyHint')}</Text>
    </Stack>
  );
}
